import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, ChevronUp, ChevronDown, RotateCcw, RefreshCw } from "lucide-react";
import { useSiteContent } from "../../context/ContentContext";
import { DEFAULT_CONTENT } from "../../lib/defaultContent";
import FieldRenderer from "./FieldRenderer";

function isBlank(value) {
  return value === null || value === undefined || (typeof value === "string" && value.trim() === "");
}

function validateField(def, value) {
  if (def?.optional) return null;
  if (def?.type === "stringlist") {
    if (!Array.isArray(value) || value.length === 0) return "Required";
    return null;
  }
  if (def?.type === "icon") {
    if (isBlank(value)) return "Required";
    if (!/^[A-Z][A-Za-z0-9]*$/.test(String(value))) return "Invalid icon name";
    return null;
  }
  if (def?.type === "image") {
    if (isBlank(value)) return "Required";
    return null;
  }
  if (def?.type === "text" || def?.type === "textarea" || !def?.type) {
    if (isBlank(value)) return "Required";
    return null;
  }
  return null;
}

function validateSchema(schema, value) {
  const errors = {};

  if (!schema) return { isValid: true, errors };

  if (schema.type === "list") {
    const items = Array.isArray(value) ? value : [];
    if (items.length === 0) errors._list = "Add at least one item";
    const fields = schema.item_fields || {};
    items.forEach((item, idx) => {
      for (const [k, def] of Object.entries(fields)) {
        const msg = validateField(def, item?.[k]);
        if (msg) errors[`${idx}.${k}`] = msg;
      }
    });
    return { isValid: Object.keys(errors).length === 0, errors };
  }

  const fields = schema.fields || {};
  for (const [k, def] of Object.entries(fields)) {
    const msg = validateField(def, value?.[k]);
    if (msg) errors[k] = msg;
  }
  return { isValid: Object.keys(errors).length === 0, errors };
}

export default function ContentEditor({ schema }) {
  const { content, save, saveSection, refreshSection } = useSiteContent();
  const initial = content?.[schema.key];
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    setValue(content?.[schema.key]);
    setDirty(false);
  }, [content, schema.key]);

  const update = (next) => {
    setValue(next);
    setDirty(true);
  };

  const validation = useMemo(() => validateSchema(schema, value), [schema, value]);

  const handleSave = async () => {
    if (!validation.isValid) {
      toast.error("Please fix validation errors before saving.");
      return;
    }
    setSaving(true);
    try {
      if (typeof saveSection === "function") {
        await saveSection(schema.key, value);
      } else {
        await save({ [schema.key]: value });
      }
      setDirty(false);
      toast.success(`${schema.label} saved.`);
    } catch (e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!confirm(`Reset ${schema.label} to default values?`)) return;
    setValue(DEFAULT_CONTENT[schema.key]);
    setDirty(true);
  };

  const handleReload = async () => {
    if (typeof refreshSection !== "function") return;
    setReloading(true);
    try {
      await refreshSection(schema.key);
      toast.success("Latest content loaded.");
    } catch {
      toast.error("Could not load latest content.");
    } finally {
      setReloading(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-[#E5E0D8] p-7" data-testid={`editor-${schema.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6 pb-5 border-b border-[#E5E0D8]">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1A1A1A]">{schema.label}</h2>
          {schema.description && (
            <p className="text-sm text-[#6B6B6B] mt-1 max-w-xl">{schema.description}</p>
          )}
        </div>
        <div className="flex gap-2 items-center">
          {dirty && (
            <span className="text-xs uppercase tracking-wider text-[#B8860B] font-semibold">
              Unsaved changes
            </span>
          )}
          <button onClick={handleReload} disabled={reloading} className="btn-secondary disabled:opacity-50" data-testid={`reload-${schema.key}`}>
            <RefreshCw className={`w-4 h-4 ${reloading ? "animate-spin" : ""}`} /> Reload
          </button>
          <button onClick={handleReset} className="btn-secondary" data-testid={`reset-${schema.key}`}>
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
          <button onClick={handleSave} disabled={saving || !dirty || !validation.isValid} className="btn-primary disabled:opacity-50" data-testid={`save-${schema.key}`}>
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {!validation.isValid && (
        <div className="mb-6 rounded-2xl border border-[#F5E1A4] bg-[#FFF6DF] px-5 py-4 text-sm text-[#7B5B00]" data-testid={`validation-${schema.key}`}>
          Fix the highlighted fields before saving.
        </div>
      )}

      {schema.type === "list" ? (
        <ListEditor
          items={Array.isArray(value) ? value : []}
          itemFields={schema.item_fields}
          onChange={update}
          schemaKey={schema.key}
          errors={validation.errors}
        />
      ) : (
        <ObjectEditor fields={schema.fields} value={value || {}} onChange={update} schemaKey={schema.key} errors={validation.errors} />
      )}
    </div>
  );
}

function ObjectEditor({ fields, value, onChange, schemaKey, errors }) {
  const setField = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {Object.entries(fields).map(([k, def]) => {
        const isFull = def.type === "textarea" || def.type === "image";
        return (
          <div key={k} className={isFull ? "md:col-span-2" : ""}>
            <FieldRenderer
              type={def.type}
              label={def.label || k}
              value={value?.[k]}
              onChange={(v) => setField(k, v)}
              testid={`field-${schemaKey}-${k}`}
              error={errors?.[k]}
            />
          </div>
        );
      })}
    </div>
  );
}

function ListEditor({ items, itemFields, onChange, schemaKey, errors }) {
  const blank = () => {
    const o = {};
    for (const [k, def] of Object.entries(itemFields)) {
      o[k] = def.type === "stringlist" ? [] : "";
    }
    return o;
  };

  const update = (idx, k, v) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [k]: v } : it));
    onChange(next);
  };
  const remove = (idx) => {
    if (!confirm("Remove this item?")) return;
    onChange(items.filter((_, i) => i !== idx));
  };
  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  const add = () => onChange([...items, blank()]);

  return (
    <div className="space-y-4">
      {errors?._list && (
        <div className="rounded-2xl border border-[#F5E1A4] bg-[#FFF6DF] px-5 py-4 text-sm text-[#7B5B00]">
          {errors._list}
        </div>
      )}
      {items.map((item, idx) => (
        <div key={idx} className="bg-[#F5F2EB] rounded-2xl p-5 border border-[#E5E0D8]" data-testid={`list-item-${schemaKey}-${idx}`}>
          <div className="flex justify-between items-center mb-4">
            <div className="text-xs uppercase tracking-[0.22em] text-[#B8860B] font-bold">Item #{idx + 1}</div>
            <div className="flex gap-1">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1.5 rounded-lg hover:bg-white disabled:opacity-30" title="Move up">
                <ChevronUp className="w-4 h-4" />
              </button>
              <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="p-1.5 rounded-lg hover:bg-white disabled:opacity-30" title="Move down">
                <ChevronDown className="w-4 h-4" />
              </button>
              <button onClick={() => remove(idx)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-600" title="Remove" data-testid={`remove-item-${schemaKey}-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(itemFields).map(([k, def]) => {
              const isFull = def.type === "textarea" || def.type === "image" || def.type === "stringlist";
              return (
                <div key={k} className={isFull ? "md:col-span-2" : ""}>
                  <FieldRenderer
                    type={def.type}
                    label={def.label || k}
                    value={item?.[k]}
                    onChange={(v) => update(idx, k, v)}
                    testid={`field-${schemaKey}-${idx}-${k}`}
                    error={errors?.[`${idx}.${k}`]}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <button onClick={add} className="btn-secondary w-full justify-center" data-testid={`add-item-${schemaKey}`}>
        <Plus className="w-4 h-4" /> Add Item
      </button>
    </div>
  );
}
